import { forwardRef, useState, type ComponentPropsWithoutRef, type FC, type ReactNode, type Ref } from "react";
import {
  CloseOutlinedIcon,
  KeyboardArrowDownOutlinedIcon,
  MoreVertOutlinedIcon,
} from "@/components/icons/chat-header-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LinearProgress } from "@/components/ui/linear-progress";
import { cn } from "@/lib/utils";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { trackUOPX } from "@/analytics";
import { UOPX_EVENT, UOPX_LOCATION, UOPX_TEXT } from "@/analytics/events";

const OPTIONS_TOOLTIP = "Options";
const CLOSE_TOOLTIP = "Close";
const MINIMIZE_TOOLTIP = "Minimize";
const HEADER_ACTION_ICON_CLASS = "h-[19px] w-[20px]";
const HEADER_ACTION_BASE_CLASS =
  "chat-header-action-btn relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[100px] border-0 bg-transparent p-[5px] text-[#001823] transition-colors " +
  "hover:bg-[rgba(0,24,35,0.12)] hover:text-[#001823] focus:bg-[rgba(0,24,35,0.12)]";
const HEADER_ACTION_GROUP_CLASS = "flex shrink-0 items-center gap-[8px]";
const HEADER_ACTION_STANDARD_CLASS = "size-[30px]";

type Props = {
  dismissAction?: "close" | "minimize";
  onGiveFeedback?: () => void;
  onNewChat?: () => void;
  onMinimize?: () => void;
  onScheduleConsultation?: () => void;
  headingRef?: Ref<HTMLHeadingElement>;
  optionsButtonRef?: Ref<HTMLButtonElement>;
};

type HeaderActionButtonProps = ComponentPropsWithoutRef<"button"> & {
  "aria-label": string;
  children: ReactNode;
  tooltip: string;
  tooltipOpen?: boolean;
  onTooltipOpenChange?: (open: boolean) => void;
};

const HeaderActionButton = forwardRef<HTMLButtonElement, HeaderActionButtonProps>(
  ({ children, className, tooltip, tooltipOpen, onTooltipOpenChange, type = "button", ...props }, ref) => (
    <Tooltip open={tooltipOpen} onOpenChange={onTooltipOpenChange}>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          type={type}
          className={cn(HEADER_ACTION_BASE_CLASS, className)}
          {...props}
        >
          {children}
          <span className="sr-only">{tooltip}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  ),
);

HeaderActionButton.displayName = "HeaderActionButton";

export const ChatHeader: FC<Props> = ({
  dismissAction = "minimize",
  onGiveFeedback,
  onNewChat,
  onMinimize,
  onScheduleConsultation,
  headingRef,
  optionsButtonRef,
}) => {
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [isOptionsTooltipOpen, setIsOptionsTooltipOpen] = useState(false);
  const isBackgroundSyncing = useBackgroundSync();
  const dismissTooltip = dismissAction === "close" ? CLOSE_TOOLTIP : MINIMIZE_TOOLTIP;

  const handleOptionsMenuOpenChange = (open: boolean) => {
    setIsOptionsMenuOpen(open);
    setIsOptionsTooltipOpen(false);
  };

  const runAfterOptionsMenuClose = (action: (() => void) | undefined) => {
    if (!action) return;
    handleOptionsMenuOpenChange(false);
    window.setTimeout(action, 0);
  };

  return (
    <header
      className="flex h-[48px] w-full shrink-0 flex-col bg-[#FFFFFF] text-foreground"
      aria-label="Chat"
    >
      <div className="flex h-[48px] items-center gap-[8px] self-stretch px-[16px] py-[7px]">
        <div className="flex flex-1 items-center">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="truncate rounded-sm text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#001823] focus:ring-offset-2"
          >
            Chat
          </h2>
        </div>

        <div className={HEADER_ACTION_GROUP_CLASS}>
          <DropdownMenu open={isOptionsMenuOpen} onOpenChange={handleOptionsMenuOpenChange} modal={false}>
            <DropdownMenuTrigger asChild>
              <HeaderActionButton
                ref={optionsButtonRef}
                tooltip={OPTIONS_TOOLTIP}
                tooltipOpen={isOptionsMenuOpen ? false : isOptionsTooltipOpen}
                onTooltipOpenChange={setIsOptionsTooltipOpen}
                aria-label={OPTIONS_TOOLTIP}
                className={HEADER_ACTION_STANDARD_CLASS}
                data-testid="aichat_window_menu"
              >
                <MoreVertOutlinedIcon className={HEADER_ACTION_ICON_CLASS} />
              </HeaderActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="aui-chat-header-options-menu z-[2147483647] min-w-[220px] rounded-sm p-0"
              // Radix focuses menu items on pointer hover, and Chrome marks
              // that focus :focus-visible — so the pseudo-class alone cannot
              // separate hover from keyboard (VIAF-446). Track the input
              // modality on the menu and let CSS gate the focus ring on it.
              data-nav="keyboard"
              onKeyDown={(event) => {
                if (["ArrowDown", "ArrowUp", "Home", "End", "Tab"].includes(event.key)) {
                  event.currentTarget.setAttribute("data-nav", "keyboard");
                }
              }}
              onPointerMove={(event) => {
                // Ignore sub-threshold jitter (a hand resting on a trackpad
                // during arrow-key navigation) so incidental movement doesn't
                // hide the keyboard focus ring.
                if (Math.abs(event.movementX) + Math.abs(event.movementY) < 2) return;
                event.currentTarget.setAttribute("data-nav", "pointer");
              }}
              onEscapeKeyDown={(event) => {
                event.stopPropagation();
                handleOptionsMenuOpenChange(false);
              }}
            >
              <DropdownMenuItem
                className="h-[48px] rounded-none border-b border-border px-[16px] text-base"
                onSelect={(event) => {
                  event.preventDefault();
                  // Track header menu selection
                  trackUOPX(UOPX_EVENT.OPTION_SELECTED, UOPX_TEXT.START_NEW_CHAT, UOPX_LOCATION.HEADER);
                  runAfterOptionsMenuClose(onNewChat);
                }}
                disabled={!onNewChat}
                data-testid="aichat_window_menu_start-new-chat"
              >
                Start a new chat
              </DropdownMenuItem>
              {onScheduleConsultation ? (
                <DropdownMenuItem
                  className="h-[48px] rounded-none border-b border-border px-[16px] text-base"
                  onSelect={(event) => {
                    event.preventDefault();
                    runAfterOptionsMenuClose(onScheduleConsultation);
                  }}
                >
                  Schedule a consultation
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                className="h-[48px] rounded-none px-[16px] text-base"
                onSelect={(event) => {
                  event.preventDefault();
                  // Track header menu selection
                  trackUOPX(UOPX_EVENT.OPTION_SELECTED, UOPX_TEXT.GIVE_FEEDBACK, UOPX_LOCATION.HEADER);
                  runAfterOptionsMenuClose(onGiveFeedback);
                }}
                disabled={!onGiveFeedback}
                data-testid="aichat_window_menu_give-feedback"
              >
                Give feedback
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <HeaderActionButton
            tooltip={dismissTooltip}
            onClick={onMinimize}
            aria-label={dismissTooltip}
            className={HEADER_ACTION_STANDARD_CLASS}
            data-testid="aichat_window_close"
          >
            {dismissAction === "close" ? (
              <CloseOutlinedIcon className={HEADER_ACTION_ICON_CLASS} />
            ) : (
              <KeyboardArrowDownOutlinedIcon className={HEADER_ACTION_ICON_CLASS} />
            )}
          </HeaderActionButton>
        </div>
      </div>
      
      <LinearProgress visible={isBackgroundSyncing} />
    </header>
  );
};
